import React, { useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useLanguage } from '@contexts/LanguageContext';
import CategoryThumbnail from '@components/attachments/CategoryThumbnail';
import './HorizontalCategoryFilter.scss';

export const HorizontalCategoryFilter = ({
    categories = [],
    selectedCategoryIds = [],
    onChange,
    loading = false,
    multiple = false
}) => {
    const { t } = useLanguage();

    const [emblaRef] = useEmblaCarousel({
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: true,
        skipSnaps: true
    });

    const handleCategoryClick = (categoryId) => {
        const currentSelection = Array.isArray(selectedCategoryIds) ? selectedCategoryIds : [];
        let newSelection;

        if (multiple) {
            // Multiple selection mode
            if (currentSelection.includes(categoryId)) {
                // Remove category if already selected
                newSelection = currentSelection.filter(id => id !== categoryId);
            } else {
                // Add category to selection
                newSelection = [...currentSelection, categoryId];
            }
        } else {
            // Single selection mode
            if (currentSelection.includes(categoryId)) {
                // If clicking the already selected category, deselect it
                newSelection = [];
            } else {
                // Select only this category
                newSelection = [categoryId];
            }
        }

        onChange('category_ids', newSelection);
    };

    const handleClearAll = () => {
        onChange('category_ids', []);
    };

    const renderEmblaSlide = (category) => {
        const isSelected = selectedCategoryIds.includes(category.id);
        const backgroundColor = category.color || '#007bff';

        // Create the fallback component for when no image is found
        const fallbackSlide = (
            <div
                className="thumbnail-image"
                style={{ backgroundColor }}
            >
                <span className="category-initial">
                    {category.name.charAt(0).toUpperCase()}
                </span>
            </div>
        );

        return (
            <div key={category.id} className="embla__slide">
                <button
                    className={`category-thumbnail ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleCategoryClick(category.id)}
                    type="button"
                    title={category.description || category.name}
                >
                    <CategoryThumbnail
                        categoryId={category.id}
                        categoryName={category.name}
                        size="medium"
                        thumbnailSize={80}
                        className="category-thumbnail-image"
                        fallbackComponent={fallbackSlide}
                    />
                    <span className="category-name">{category.name}</span>
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="horizontal-category-filter">
                <div className="embla">
                    <div className="embla__container">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="embla__slide">
                                <div className="category-thumbnail skeleton">
                                    <div className="thumbnail-skeleton"></div>
                                    <span className="category-name-skeleton"></span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!categories || categories.length === 0) {
        return null;
    }

    const hasSelection = selectedCategoryIds && selectedCategoryIds.length > 0;
    const showClearButton = hasSelection && (multiple || selectedCategoryIds.length > 1);

    return (
        <div className="horizontal-category-filter">
            <div className="category-filter-header">
                {showClearButton && (
                    <button
                        className="clear-selection-btn"
                        onClick={handleClearAll}
                        type="button"
                    >
                        {t('collection.clearCategoriesFilters')}
                    </button>
                )}
            </div>

            <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                    {categories.map(renderEmblaSlide)}
                </div>
            </div>
        </div>
    );
};

export default HorizontalCategoryFilter;
